import { useId, useState, type KeyboardEvent } from 'react'

import { useFormFieldAria } from '@ui/components/form-field'
import { Input } from '@ui/components/ui/input'
import { Popover, PopoverAnchor, PopoverContent } from '@ui/components/ui/popover'
import { Skeleton } from '@ui/components/ui/skeleton'
import { cn } from '@ui/lib/utils'

export interface ComboboxProps<T> {
  /**
   * id des Eingabefelds. In einem {@link FormField} mit gleichem `htmlFor`
   * beschriftet dessen Label das Feld; der Platzhalter dient dann nicht mehr
   * als Name.
   */
  inputId?: string
  /** Optional input length limit for server-side searches. */
  maxLength?: number
  /** Die Vorschläge. Kommen fertig gefiltert vom Server — es wird nicht nachgefiltert. */
  items: T[]
  /** Der ausgewählte Eintrag, oder `null`. */
  value: T | null
  /** Wird bei Auswahl gerufen. */
  onChange: (item: T) => void
  /** Wird bei jeder Eingabe gerufen. Hier die Serversuche anstoßen (entprellt). */
  onSearch: (search: string) => void
  /** Eindeutiger Schlüssel eines Eintrags. */
  getKey: (item: T) => string
  /** Was in der Zeile steht. */
  getLabel: (item: T) => string
  /** Optionale zweite Zeile, z.B. die E-Mail-Adresse. */
  getDescription?: (item: T) => string
  placeholder: string
  /** Was steht da, wenn nichts gefunden wurde. */
  emptyText: string
  /** Während die Serversuche läuft. */
  loading?: boolean
  /**
   * Gesteuerter Eingabetext. Damit wird die Combobox zum Freitextfeld mit
   * Vorschlägen: Der Text gehört dem Aufrufer, jede Eingabe kommt über
   * `onInputValueChange`, und eine Auswahl ändert den Text nicht von selbst.
   * Die Vorschlagsliste öffnet sich erst beim Tippen, nicht schon beim
   * Betreten des Felds.
   */
  inputValue?: string
  /** Wird bei jeder Eingabe mit dem neuen Text gerufen. Gehört zu `inputValue`. */
  onInputValueChange?: (text: string) => void
  disabled?: boolean
  required?: boolean
  /** Markiert das Feld als fehlerhaft (`aria-invalid`). */
  invalid?: boolean
  inputMode?: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url'
  autoComplete?: string
}

/**
 * Suchfeld mit Vorschlagsliste.
 *
 * Die Treffer kommen vom Server, nicht aus dem Browser: `onSearch` meldet jede
 * Eingabe nach außen, und `items` wird unverändert angezeigt. Deshalb bewusst
 * ohne `cmdk` — dessen Stärke ist das Filtern im Browser, das hier gerade nicht
 * gebraucht wird.
 *
 * Zwei Arten: Ohne `inputValue` ist sie eine reine Auswahl (etwa einer
 * Person). Mit `inputValue` ist sie ein Freitextfeld mit Vorschlägen: Getippt
 * werden darf alles, eine Auswahl füllt über `onChange` andere Felder, etwa
 * die Anschrift eines Debitors aus NAV.
 */
export function Combobox<T>({
  inputId,
  maxLength,
  items,
  value,
  onChange,
  onSearch,
  getKey,
  getLabel,
  getDescription,
  placeholder,
  emptyText,
  loading = false,
  inputValue,
  onInputValueChange,
  disabled,
  required,
  invalid,
  inputMode,
  autoComplete,
}: ComboboxProps<T>) {
  const [offen, setOffen] = useState(false)
  const [eigenerText, setEigenerText] = useState('')
  const [aktiv, setAktiv] = useState(-1)
  const listenId = useId()
  const { describedBy } = useFormFieldAria()
  const freitext = inputValue !== undefined
  const text = freitext ? inputValue : eigenerText

  function waehle(item: T) {
    onChange(item)
    if (!freitext) setEigenerText(getLabel(item))
    setOffen(false)
    setAktiv(-1)
  }

  function beiTaste(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOffen(true)
      setAktiv((vorher) => Math.min(vorher + 1, items.length - 1))
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setAktiv((vorher) => Math.max(vorher - 1, 0))
      return
    }
    if (event.key === 'Enter' && aktiv >= 0 && items[aktiv]) {
      event.preventDefault()
      waehle(items[aktiv])
      return
    }
    if (event.key === 'Escape') {
      setOffen(false)
      setAktiv(-1)
    }
  }

  return (
    <Popover open={offen} onOpenChange={setOffen}>
      <PopoverAnchor asChild>
        <Input
          id={inputId}
          maxLength={maxLength}
          role="combobox"
          // Der Platzhalter verschwindet, sobald jemand tippt. Als Name muss er
          // dauerhaft am Feld hängen, sonst heißt es ab dem ersten Zeichen nur
          // noch „Eingabefeld". Mit inputId beschriftet das Label des FormField.
          aria-label={inputId ? undefined : placeholder}
          aria-expanded={offen}
          aria-controls={listenId}
          aria-autocomplete="list"
          aria-activedescendant={
            aktiv >= 0 && items[aktiv] ? `${listenId}-${getKey(items[aktiv])}` : undefined
          }
          aria-describedby={describedBy}
          aria-required={required || undefined}
          aria-invalid={invalid || undefined}
          placeholder={placeholder}
          value={text}
          disabled={disabled}
          required={required}
          inputMode={inputMode}
          autoComplete={autoComplete}
          // Im Freitextfeld nicht beim Betreten öffnen: Wer ein gefülltes Feld
          // anklickt, will meist nur korrigieren, nicht suchen.
          onFocus={() => {
            if (!freitext) setOffen(true)
          }}
          onChange={(event) => {
            const neu = event.target.value
            if (freitext) onInputValueChange?.(neu)
            else setEigenerText(neu)
            setAktiv(-1)
            setOffen(!freitext || neu.trim() !== '')
            onSearch(neu)
          }}
          onKeyDown={beiTaste}
        />
      </PopoverAnchor>

      <PopoverContent
        align="start"
        className="w-(--radix-popover-trigger-width) min-w-64 p-1"
        // Der Fokus bleibt im Eingabefeld: Sonst könnte man nach dem Öffnen
        // nicht weitertippen, und die Tastaturbedienung wäre zerrissen.
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {loading ? (
          <div className="space-y-1 p-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-5 w-2/3" />
          </div>
        ) : items.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">{emptyText}</p>
        ) : (
          <ul id={listenId} role="listbox" className="max-h-72 overflow-y-auto">
            {items.map((item, index) => (
              <li
                key={getKey(item)}
                id={`${listenId}-${getKey(item)}`}
                role="option"
                aria-selected={value !== null && getKey(value) === getKey(item)}
                // 44px Trefferfläche — das Portal wird teils mit Handschuhen bedient.
                className={cn(
                  'flex min-h-11 cursor-pointer flex-col justify-center rounded-md px-3 py-1.5 text-sm',
                  index === aktiv ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60',
                )}
                onMouseEnter={() => setAktiv(index)}
                // Bewusst onMouseDown und nicht onClick: Der Fokus liegt im
                // Eingabefeld. Ein Klick würde ihn erst wegnehmen, die Liste
                // schließen und dann erst auslösen. preventDefault lässt den
                // Fokus, wo er ist. Die Tastaturbedienung läuft ohnehin über das
                // Eingabefeld und aria-activedescendant.
                onMouseDown={(event) => {
                  event.preventDefault()
                  waehle(item)
                }}
              >
                <span>{getLabel(item)}</span>
                {getDescription && (
                  <span className="text-xs text-muted-foreground">
                    {getDescription(item)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  )
}
